"use client";

import { useState } from "react";
import { Button } from "@/components/ui/button";
import { isOfficeHost, signInWithDialog } from "@/lib/auth";
import { resolveTenant } from "@/lib/tenant";
import { TENANTS } from "@/lib/tenants";
import { captureError } from "@/lib/telemetry";

export function AuthGate({
  onSignedIn,
  onSwitchWorkspace,
}: {
  onSignedIn: (mirrorId: string) => void;
  onSwitchWorkspace?: () => void;
}) {
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const inOfficeHost = isOfficeHost();
  const tenantId = resolveTenant();
  const tenantName = tenantId ? TENANTS[tenantId].displayName : null;

  async function handleSignIn() {
    setPending(true);
    setError(null);
    try {
      const { mirrorId } = await signInWithDialog();
      onSignedIn(mirrorId);
    } catch (e) {
      const msg = e instanceof Error ? e.message : "Sign-in failed";
      setError(msg);
      captureError(e, { where: "AuthGate.signIn" });
    } finally {
      setPending(false);
    }
  }

  return (
    <div className="flex h-full flex-col items-center justify-center gap-6 px-6 text-center">
      <img src="/logo.svg" alt="myai" width={64} height={64} />
      <div className="flex flex-col gap-2">
        <h1 className="text-lg font-semibold text-[var(--text-primary)]">myai for Excel</h1>
        <p className="text-sm text-[var(--text-secondary)]">
          Chat with your myai agent about the workbook in front of you.
        </p>
      </div>
      {inOfficeHost ? (
        <Button onClick={handleSignIn} disabled={pending}>
          {pending ? "Signing in…" : "Sign in to myai"}
        </Button>
      ) : (
        <div className="flex flex-col gap-2 max-w-sm">
          <p className="text-sm text-[var(--text-secondary)]">
            This add-in runs inside Excel.
          </p>
          <p className="text-xs text-[var(--text-muted)]">
            Open Excel → Insert → Add-ins → My Add-ins → upload the manifest, or click the
            myai ribbon button if it&apos;s already installed.
          </p>
        </div>
      )}
      {error && (
        <p role="alert" className="text-sm text-[var(--status-error)]">
          {error}
        </p>
      )}
      {onSwitchWorkspace && tenantName && (
        <div className="flex flex-col items-center gap-1 text-xs text-[var(--text-muted)]">
          <span>
            Signing into <span className="text-[var(--text-secondary)]">{tenantName}</span>
          </span>
          <button
            type="button"
            onClick={onSwitchWorkspace}
            className="text-[var(--brand-accent)] hover:underline"
          >
            Switch workspace
          </button>
        </div>
      )}
    </div>
  );
}
